import React from 'react';
import { motion } from 'framer-motion';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { 
  FileText, 
  Clock, 
  CheckCircle, 
  XCircle, 
  AlertCircle,
  Users,
  TrendingUp,
  Calendar
} from 'lucide-react';
import { timesheetAPI, userAPI } from '../services/api';
import { format } from 'date-fns';

const AdminDashboard = () => {
  const navigate = useNavigate();

  // Optimized: Single endpoint for dashboard stats (counts only)
  const { data: dashboardStats, isLoading: statsLoading } = useQuery(
    'admin-dashboard-stats',
    () => userAPI.getAdminDashboardStats(),
    {
      select: (response) => response.data,
      staleTime: 5 * 60 * 1000, // Cache for 5 minutes
      cacheTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
    }
  );

  // Optimized: Recent timesheets (top 5 only)
  const { data: recentTimesheetsData, isLoading: recentTimesheetsLoading } = useQuery(
    'admin-recent-timesheets',
    () => userAPI.getAdminRecentTimesheets(),
    {
      select: (response) => response.data?.timesheets || [],
      staleTime: 2 * 60 * 1000, // Cache for 2 minutes
      cacheTime: 5 * 60 * 1000,
    }
  );

  const { data: pendingTimesheets, isLoading: pendingLoading } = useQuery(
    'admin-pending-timesheets',
    () => timesheetAPI.getPendingTimesheets(),
    {
      select: (response) => response.data.timesheets,
      staleTime: 1 * 60 * 1000, // Cache for 1 minute
      cacheTime: 5 * 60 * 1000,
    }
  );

  const getStatusIcon = (status) => {
    switch (status) {
      case 'DRAFT':
        return <FileText className="w-5 h-5 text-gray-500" />;
      case 'SUBMITTED':
        return <Clock className="w-5 h-5 text-blue-500" />;
      case 'PENDING':
        return <AlertCircle className="w-5 h-5 text-yellow-500" />;
      case 'APPROVED':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'REJECTED':
        return <XCircle className="w-5 h-5 text-red-500" />;
      default:
        return <FileText className="w-5 h-5 text-gray-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'DRAFT':
        return 'bg-gray-100 text-gray-800';
      case 'SUBMITTED':
        return 'bg-blue-100 text-blue-800';
      case 'PENDING':
        return 'bg-yellow-100 text-yellow-800';
      case 'APPROVED':
        return 'bg-green-100 text-green-800';
      case 'REJECTED':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Calculate stats from optimized endpoint
  const stats = [
    {
      name: 'Total Timesheets',
      value: dashboardStats?.totalTimesheets || 0,
      icon: FileText,
      color: 'text-purple-600',
      bgColor: 'bg-purple-100',
      clickable: true,
      navigateTo: '/admin/timesheets',
    },
    {
      name: 'Submitted Timesheets',
      value: dashboardStats?.submittedTimesheets || 0,
      icon: Clock,
      color: 'text-blue-600',
      bgColor: 'bg-blue-100',
      clickable: true,
      navigateTo: '/admin/timesheets?status=SUBMITTED',
    },
    {
      name: 'Approved Timesheets',
      value: dashboardStats?.approvedTimesheets || 0,
      icon: CheckCircle,
      color: 'text-green-600',
      bgColor: 'bg-green-100',
      clickable: true,
      navigateTo: '/admin/timesheets?status=APPROVED',
    },
  ];

  // Show loading only for critical stats, allow progressive rendering
  if (statsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-lg shadow-sm p-6"
      >
        <h1 className="text-2xl font-bold text-gray-900">Admin Dashboard</h1>
        <p className="text-gray-600 mt-1">
          Manage timesheets and employee approvals
        </p>
      </motion.div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {stats.map((stat, index) => (
          <motion.div
            key={stat.name}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (stat.clickable && stat.navigateTo) {
                console.log('Navigating to:', stat.navigateTo);
                navigate(stat.navigateTo);
              }
            }}
            className={`bg-white rounded-lg shadow-sm p-6 ${
              stat.clickable
                ? 'cursor-pointer hover:shadow-md transition-shadow'
                : ''
            }`}
            whileHover={stat.clickable ? { scale: 1.02 } : {}}
            whileTap={stat.clickable ? { scale: 0.98 } : {}}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className={`p-3 rounded-lg ${stat.bgColor}`}>
                  <stat.icon className={`w-6 h-6 ${stat.color}`} />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">{stat.name}</p>
                  <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                </div>
              </div>
              {stat.clickable && (
                <div className="text-xs text-gray-400">
                  Click to view
                </div>
              )}
            </div>
          </motion.div>
        ))}
      </div>

      {/* Quick Actions */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="bg-white rounded-lg shadow-sm p-6"
      >
        <h2 className="text-lg font-medium text-gray-900 mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => navigate('/admin/timesheets/review')}
            className="p-4 border border-gray-200 rounded-lg hover:border-primary-300 hover:bg-primary-50 transition-colors text-left"
          >
            <FileText className="w-8 h-8 text-primary-600 mb-2" />
            <h3 className="font-medium text-gray-900">Review Timesheets</h3>
            <p className="text-sm text-gray-500">Approve or reject submitted timesheets</p>
          </motion.button>
          
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => navigate('/admin/employees')}
            className="p-4 border border-gray-200 rounded-lg hover:border-primary-300 hover:bg-primary-50 transition-colors text-left"
          >
            <Users className="w-8 h-8 text-primary-600 mb-2" />
            <h3 className="font-medium text-gray-900">Manage Employees</h3>
            <p className="text-sm text-gray-500">Add or edit employee accounts</p>
          </motion.button>
          
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => navigate('/admin/consolidated-export')}
            className="p-4 border border-gray-200 rounded-lg hover:border-primary-300 hover:bg-primary-50 transition-colors text-left"
          >
            <TrendingUp className="w-8 h-8 text-primary-600 mb-2" />
            <h3 className="font-medium text-gray-900">Consolidation</h3>
            <p className="text-sm text-gray-500">Generate consolidated timesheet reports</p>
          </motion.button>
        </div>
      </motion.div>

      {/* Consolidated Timesheet Export Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.45 }}
        className="bg-white rounded-lg shadow-sm p-6"
      >
        <h2 className="text-lg font-medium text-gray-900 mb-4">Consolidation</h2>
        <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => navigate('/admin/consolidated-export')}
            className="p-4 border border-gray-200 rounded-lg hover:border-primary-300 hover:bg-primary-50 transition-colors text-left"
          >
            <FileText className="w-8 h-8 text-primary-600 mb-2" />
            <h3 className="font-medium text-gray-900">Consolidated Timesheet Report</h3>
            <p className="text-sm text-gray-500">Generate consolidated PDF report for single or all projects</p>
          </motion.button>
        </div>
      </motion.div>

      {/* Pending Timesheets */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="bg-white rounded-lg shadow-sm"
      >
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-lg font-medium text-gray-900">Pending Approvals</h2>
          <button
            onClick={() => navigate('/admin/timesheets')}
            className="text-sm text-primary-600 hover:text-primary-500 font-medium"
          >
            View All
          </button>
        </div>
        <div className="divide-y divide-gray-200">
          {pendingTimesheets?.slice(0, 5).map((timesheet, index) => (
            <motion.div
              key={timesheet.id}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.6 + index * 0.1 }}
              className="px-6 py-4 hover:bg-gray-50 cursor-pointer"
              onClick={() => {
                console.log('AdminDashboard - Clicking timesheet:', timesheet.id);
                console.log('AdminDashboard - Navigating to:', `/admin/timesheets/${timesheet.id}`);
                navigate(`/admin/timesheets/${timesheet.id}`);
              }}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  {getStatusIcon(timesheet.status)}
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {timesheet.employee.firstName} {timesheet.employee.lastName}
                    </p>
                    <p className="text-sm text-gray-500">
                      {format(new Date(timesheet.startDate), 'MMM dd')} - {format(new Date(timesheet.endDate), 'MMM dd, yyyy')}
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-4">
                  <span className="text-sm text-gray-500">
                    {timesheet.totalHours} hours
                  </span>
                  <span className="text-sm text-gray-500">
                    {format(new Date(timesheet.submittedAt), 'MMM dd, yyyy')}
                  </span>
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(timesheet.status)}`}>
                    {timesheet.status}
                  </span>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
        {pendingTimesheets?.length === 0 && (
          <div className="px-6 py-8 text-center">
            <CheckCircle className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No pending approvals</h3>
            <p className="mt-1 text-sm text-gray-500">All timesheets are up to date.</p>
          </div>
        )}
      </motion.div>

      {/* Recent Activity */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.7 }}
        className="bg-white rounded-lg shadow-sm"
      >
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">Recent Activity</h2>
          <div className="mt-2">
            <a 
              href="/admin/timesheets/1" 
              className="text-blue-600 hover:text-blue-800 text-sm underline"
              onClick={(e) => {
                e.preventDefault();
                console.log('Testing direct navigation to /admin/timesheets/1');
                navigate('/admin/timesheets/1');
              }}
            >
              Test Direct Link to Timesheet 1
            </a>
          </div>
        </div>
        <div className="divide-y divide-gray-200">
          {/* Clickable rows indicator */}
          <div className="px-6 py-2 bg-blue-50 border-b border-blue-200">
            <p className="text-xs text-blue-600 flex items-center">
              <FileText className="w-3 h-3 mr-1" />
              Click on any timesheet row to view full details
            </p>
          </div>
          {recentTimesheetsLoading ? (
            <div className="px-6 py-8 text-center">
              <div className="spinner mx-auto"></div>
            </div>
          ) : recentTimesheetsData?.length > 0 ? (
            recentTimesheetsData.map((timesheet, index) => (
            <motion.div
              key={timesheet.id}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.8 + index * 0.1 }}
              onClick={() => {
                console.log('Admin Dashboard - Navigating to timesheet:', timesheet.id);
                navigate(`/admin/timesheets/${timesheet.id}`);
              }}
              className="px-6 py-4 hover:bg-gray-50 hover:border-l-4 hover:border-primary-500 transition-all duration-200 cursor-pointer group"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  {getStatusIcon(timesheet.status)}
                  <div>
                    <p className="text-sm font-medium text-gray-900 group-hover:text-primary-600 transition-colors flex items-center">
                      {timesheet.employee.firstName} {timesheet.employee.lastName}
                      <span className="ml-2 text-gray-400 group-hover:text-primary-500 transition-colors">
                        →
                      </span>
                    </p>
                    <p className="text-sm text-gray-500">
                      {format(new Date(timesheet.startDate), 'MMM dd')} - {format(new Date(timesheet.endDate), 'MMM dd, yyyy')}
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-4">
                  <span className="text-sm text-gray-500">
                    {timesheet.totalHours} hours
                  </span>
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(timesheet.status)}`}>
                    {timesheet.status}
                  </span>
                </div>
                </div>
              </motion.div>
            ))
          ) : (
            <div className="px-6 py-8 text-center">
              <FileText className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No recent activity</h3>
              <p className="mt-1 text-sm text-gray-500">No timesheets found.</p>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default AdminDashboard;

